// src/pages/Apps.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AppModal from '../components/AppModal';
import Card from '../components/Card';
import PageHeader from '../components/PageHeader';
import PrimaryButton from '../components/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton';
import SearchInput from '../components/SearchInput';
import SortSelect from '../components/SortSelect';
import StatusBadge from '../components/StatusBadge';
import StatusSegmented from '../components/StatusSegmented';
import InfoCard from '../components/InfoCard';
import EmptyState from '../components/EmptyState';
import ErrorAlert from '../components/ErrorAlert';
import Skeleton, { SkeletonTableRows, SkeletonListToolbar, SkeletonListCard } from '../components/Skeleton';
import LoadingAnnouncer from '../components/LoadingAnnouncer';
import DeleteModal from '../components/DeleteModal';
import DecoyAppsPanel from '../components/DecoyAppsPanel';
import { ROVING_ITEM_ATTR, onRovingKeyDown, rovingTabIndex } from '../components/rovingFocus';
import { HeaderCell, BodyCell } from '../components/TableCell';
import {
  IconPlus, IconRefresh, IconGrip, IconArrowUp, IconArrowDown,
  IconEdit, IconTrash, IconAlertTriangle,
} from '../icons';
import { formatDate } from '../utils/format';

const COL = {
  handle: 52,
  name: 260,
  status: 132,
  version: 100,
  proxy: 124,
  url: 240,
  checked: 190,
  unavailable: 190,
  actions: 96,
};

const SORT_OPTIONS = [
  { value: 'status', label: 'Status first' },
  { value: 'manual', label: 'Manual order' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
  { value: 'checked_desc', label: 'Last checked' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'available', label: 'Available' },
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'unknown', label: 'Unknown' },
];

const STORE_TYPE_LABELS = {
  appstore: 'App Store',
  googleplay: 'Google Play',
  other: 'Other',
};

const TABS = [
  { value: 'monitored', label: 'Monitored' },
  { value: 'decoys', label: 'Decoys' },
];

const PageTabs = ({ value, onChange }) => {
  const activeIndex = TABS.findIndex((tab) => tab.value === value);
  return (
    <div role="tablist" aria-label="App lists" onKeyDown={onRovingKeyDown} className="flex gap-1 border-b border-line">
      {TABS.map((tab, index) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={`apps-tab-${tab.value}`}
            aria-selected={active}
            aria-controls="apps-tabpanel"
            tabIndex={rovingTabIndex(active, index, activeIndex)}
            {...{ [ROVING_ITEM_ATTR]: '' }}
            onClick={() => onChange(tab.value)}
            className={`-mb-px px-3 py-2 max-md:min-h-11 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ${
              active ? 'border-brand-500 text-ink' : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

const STATUS_PRIORITY = { unavailable: 0, unknown: 1, available: 2 };

const ROW = 'flex items-stretch h-16 border-b border-line/60 last:border-b-0 hover:bg-surface transition-colors';

// Те же колонки, что у SortableAppRow: ширины из COL, в колонке App — иконка
// 36px и две строки, как у настоящей ячейки.
const SKELETON_COLUMNS = [
  { width: COL.handle, first: true, fill: 30, height: 16 },
  {
    width: COL.name,
    grow: true,
    align: 'left',
    content: (
      <div className="flex items-center gap-2.5 min-w-0 w-full">
        <Skeleton width={36} height={36} radius="md" />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <Skeleton width="55%" height={13} />
          <Skeleton width="35%" height={11} />
        </div>
      </div>
    ),
  },
  { width: COL.status, fill: 65, height: 24, radius: 'full' },
  { width: COL.version, fill: 50, height: 20, radius: 'full' },
  { width: COL.proxy, fill: 60, height: 24, radius: 'full' },
  { width: COL.url, grow: true, align: 'left', fill: 70 },
  { width: COL.checked, fill: 75 },
  { width: COL.unavailable, fill: 75 },
  { width: COL.actions, sticky: 'right', fill: 60, height: 16 },
];

// Reflects the populated `proxy` ref from GET /api/apps. `app.proxyStatus`
// (used by the creating-poll effect below) is not part of the App schema, so
// it is not shown here.
const ProxyBadge = ({ app }) => {
  if (!app.proxy) return <span className="text-ink-muted">none</span>;
  const faulty = app.proxy.isFaulty;
  return (
    <span title={faulty ? app.proxy.lastErrorMessage || 'Proxy is faulty' : app.proxy.location || ''}>
      <StatusBadge status={faulty ? 'faulty' : 'healthy'} label={faulty ? 'faulty' : 'healthy'} />
    </span>
  );
};

const SortableAppRow = ({
  app, index, totalCount, dragEnabled,
  onNavigate, onEdit, onDelete, onMoveUp, onMoveDown,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: app._id, disabled: !dragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const iconSrc = app.iconPath || app.iconUrl;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${ROW} cursor-pointer`}
      onClick={() => onNavigate(`/apps/${app._id}`)}
    >
      <BodyCell width={COL.handle} first>
        {dragEnabled ? (
          <div
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="text-ink-muted hover:text-ink cursor-grab active:cursor-grabbing"
            aria-label="Drag to reorder"
          >
            <IconGrip size={16} />
          </div>
        ) : (
          <div className="flex items-center gap-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onMoveUp(index); }}
              disabled={index === 0}
              aria-label="Move up"
              className="w-7 h-7 inline-flex items-center justify-center rounded text-ink-muted hover:text-ink disabled:text-ink-disabled disabled:cursor-not-allowed"
            >
              <IconArrowUp size={12} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onMoveDown(index); }}
              disabled={index === totalCount - 1}
              aria-label="Move down"
              className="w-7 h-7 inline-flex items-center justify-center rounded text-ink-muted hover:text-ink disabled:text-ink-disabled disabled:cursor-not-allowed"
            >
              <IconArrowDown size={12} />
            </button>
          </div>
        )}
      </BodyCell>

      <BodyCell width={COL.name} grow align="left">
        <div className="flex items-center gap-2.5 min-w-0">
          {iconSrc ? (
            <img
              src={iconSrc}
              alt=""
              className="w-9 h-9 rounded-lg object-cover border border-line shrink-0"
            />
          ) : (
            <span className="w-9 h-9 rounded-lg bg-surface border border-line shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-ink truncate">{app.name}</p>
            <p className="text-[11px] text-ink-muted truncate">
              {app.description || STORE_TYPE_LABELS[app.storeType] || ''}
            </p>
          </div>
        </div>
      </BodyCell>

      <BodyCell width={COL.status}>
        <StatusBadge status={app.lastCheckStatus} />
      </BodyCell>

      <BodyCell width={COL.version}>
        {app.storeType === 'appstore' && app.lastKnownVersion ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand-50 text-brand-600">
            v{app.lastKnownVersion}
          </span>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </BodyCell>

      <BodyCell width={COL.proxy}>
        <ProxyBadge app={app} />
      </BodyCell>

      <BodyCell width={COL.url} grow align="left">
        <a
          href={app.storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-brand-500 hover:underline truncate"
          title={app.storeUrl}
        >
          {app.storeUrl}
        </a>
      </BodyCell>

      <BodyCell width={COL.checked}>
        <span className="text-ink-muted whitespace-nowrap">
          {formatDate(app.lastCheckedAt)}
        </span>
      </BodyCell>

      <BodyCell width={COL.unavailable}>
        <span className="text-ink-muted whitespace-nowrap">
          {formatDate(app.lastUnavailableCheckAt)}
        </span>
      </BodyCell>

      <BodyCell width={COL.actions} sticky="right">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(app); }}
            aria-label="Edit app"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-brand-500 hover:bg-surface transition-colors"
          >
            <IconEdit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(app); }}
            aria-label="Delete app"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-danger hover:bg-surface transition-colors"
          >
            <IconTrash size={16} />
          </button>
        </div>
      </BodyCell>
    </div>
  );
};

const Apps = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'decoys' ? 'decoys' : 'monitored';
  const setTab = (value) => setSearchParams(value === 'decoys' ? { tab: 'decoys' } : {}, { replace: true });
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [proxyServiceStatus, setProxyServiceStatus] = useState({ isDown: false, description: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('status');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/apps');
      if (response.data.success) {
        setApps(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch apps');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProxyServiceStatus = async () => {
    try {
      const response = await axios.get('/api/apps/proxy-service-status');
      if (response.data.success) {
        setProxyServiceStatus(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch proxy service status:', err);
    }
  };

  useEffect(() => {
    fetchApps();
    fetchProxyServiceStatus();

    // Poll proxy service status every 60 seconds
    const statusInterval = setInterval(fetchProxyServiceStatus, 60000);
    return () => clearInterval(statusInterval);
  }, []);

  // Auto-refresh if any app is in 'creating' status
  useEffect(() => {
    const hasCreatingProxy = apps.some(app => app.proxyStatus === 'creating');

    if (hasCreatingProxy) {
      const interval = setInterval(() => {
        fetchApps();
      }, 5000); // Refresh every 5 seconds

      return () => clearInterval(interval);
    }
  }, [apps]);

  const counts = useMemo(() => ({
    all: apps.length,
    available: apps.filter((a) => a.lastCheckStatus === 'available').length,
    unavailable: apps.filter((a) => a.lastCheckStatus === 'unavailable').length,
    unknown: apps.filter((a) => a.lastCheckStatus !== 'available' && a.lastCheckStatus !== 'unavailable').length,
  }), [apps]);

  // Search / filter / sort are applied client-side — the list is small and the
  // API returns everything in one call.
  const visibleApps = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = apps.filter((a) => {
      if (statusFilter !== 'all') {
        const status = a.lastCheckStatus === 'available' || a.lastCheckStatus === 'unavailable'
          ? a.lastCheckStatus
          : 'unknown';
        if (status !== statusFilter) return false;
      }
      if (!q) return true;
      return [a.name, a.description, a.storeUrl]
        .some((v) => v && v.toLowerCase().includes(q));
    });

    if (sort === 'status') {
      list = [...list].sort((a, b) =>
        (STATUS_PRIORITY[a.lastCheckStatus] ?? 1) - (STATUS_PRIORITY[b.lastCheckStatus] ?? 1));
    } else if (sort === 'name_asc') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'name_desc') {
      list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    } else if (sort === 'checked_desc') {
      list = [...list].sort((a, b) =>
        new Date(b.lastCheckedAt || 0) - new Date(a.lastCheckedAt || 0));
    }
    return list;
  }, [apps, search, sort, statusFilter]);

  // Reordering only makes sense while the list mirrors the stored order.
  const dragEnabled = sort === 'manual' && statusFilter === 'all' && !search.trim();

  const handleCreateApp = () => {
    setEditingApp(null);
    setShowModal(true);
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    const app = deleteTarget;
    setDeleteTarget(null);
    if (!app) return;
    try {
      const response = await axios.delete(`/api/apps/${app._id}`);
      if (response.data.success) {
        fetchApps();
      }
    } catch (err) {
      setError('Failed to delete app');
      console.error(err);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setEditingApp(null);
    if (shouldRefresh) {
      fetchApps();
    }
  };

  const handleCheckAll = async () => {
    try {
      setCheckingAll(true);
      const response = await axios.post('/api/apps/check-all');
      if (response.data.data && response.data.data.proxyServiceDown) {
        setError('Proxy service is down — checks are disabled');
        setCheckingAll(false);
        fetchProxyServiceStatus();
        return;
      }
      // Wait a bit for checks to complete, then refresh
      setTimeout(() => {
        fetchApps();
        setCheckingAll(false);
      }, 2000);
    } catch (err) {
      console.error('Error checking all apps:', err);
      setError('Failed to check all apps');
      setCheckingAll(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const persistOrder = async (ordered) => {
    setApps(ordered);
    try {
      await axios.put('/api/apps/reorder', {
        apps: ordered.map((app, index) => ({ id: app._id, order: index })),
      });
    } catch (err) {
      console.error('Error updating app order:', err);
      fetchApps();
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = apps.findIndex((a) => a._id === active.id);
    const newIndex = apps.findIndex((a) => a._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    persistOrder(arrayMove(apps, oldIndex, newIndex));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    persistOrder(arrayMove(apps, index, index - 1));
  };

  const handleMoveDown = (index) => {
    if (index === apps.length - 1) return;
    persistOrder(arrayMove(apps, index, index + 1));
  };

  const addAppButton = (
    <PrimaryButton onClick={handleCreateApp}>
      <IconPlus size={14} />
      Add app
    </PrimaryButton>
  );

  const actions = (
    <>
      <SecondaryButton
        onClick={handleCheckAll}
        loading={checkingAll}
        disabled={proxyServiceStatus.isDown}
        title={proxyServiceStatus.isDown ? 'Checks disabled — proxy service is down' : ''}
      >
        {!checkingAll && <IconRefresh size={14} />}
        {checkingAll ? 'Checking…' : 'Check all'}
      </SecondaryButton>
      {addAppButton}
    </>
  );

  const tableHeader = (
    <div className="flex items-stretch h-10 bg-surface border-b border-line">
      <HeaderCell width={COL.handle} first>#</HeaderCell>
      <HeaderCell width={COL.name} grow align="left">App</HeaderCell>
      <HeaderCell width={COL.status}>Status</HeaderCell>
      <HeaderCell width={COL.version}>Version</HeaderCell>
      <HeaderCell width={COL.proxy}>Proxy</HeaderCell>
      <HeaderCell width={COL.url} grow align="left">Store URL</HeaderCell>
      <HeaderCell width={COL.checked}>Last checked</HeaderCell>
      <HeaderCell width={COL.unavailable}>Last unavailable</HeaderCell>
      <HeaderCell width={COL.actions} sticky="right" />
    </div>
  );

  return (
    <div className="bg-page">
      <div className="w-full px-4 sm:px-8 flex flex-col gap-5">
        <PageHeader title="App Monitoring" actions={tab === 'monitored' ? actions : null} />
        <PageTabs value={tab} onChange={setTab} />
        {tab === 'monitored' && (
          <LoadingAnnouncer loading={loading} label="Loading apps" loadedLabel="Apps loaded" />
        )}

        <ErrorAlert message={error} onDismiss={() => setError('')} />

        {proxyServiceStatus.isDown && (
          <ErrorAlert
            variant="warning"
            icon={<IconAlertTriangle size={16} />}
            message={`Proxy service is down — ${proxyServiceStatus.description || 'service is experiencing issues'}. All checks are paused.`}
          />
        )}

        <div id="apps-tabpanel" role="tabpanel" aria-labelledby={`apps-tab-${tab}`} className="flex flex-col gap-5">
        {tab === 'decoys' ? (
          <DecoyAppsPanel />
        ) : loading ? (
          // Та же раскладка, что у готового списка: полоса InfoCard с настоящими
          // подписями, тулбар, шапка таблицы и строки той же высоты.
          <>
            <div className="flex flex-wrap gap-3">
              {['Total', 'Available', 'Unavailable', 'Unknown', 'Proxy service'].map((label) => (
                <InfoCard key={label} label={label} loading />
              ))}
            </div>
            <Card className="flex flex-col">
              <SkeletonListToolbar />
              <div className="hidden md:block overflow-x-auto">
                <div className="min-w-[1380px]">
                  {tableHeader}
                  <SkeletonTableRows columns={SKELETON_COLUMNS} rows={6} rowClassName={ROW} />
                </div>
              </div>
              <div className="md:hidden divide-y divide-line">
                {Array.from({ length: 3 }, (_, index) => <SkeletonListCard key={index} withIcon />)}
              </div>
            </Card>
          </>
        ) : apps.length === 0 ? (
          <Card>
            <EmptyState
              title="No apps yet"
              description="Get started by adding an app to monitor."
              action={addAppButton}
            />
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap gap-3">
              <InfoCard label="Total" value={counts.all} />
              <InfoCard label="Available" value={counts.available} accent="text-success" />
              <InfoCard label="Unavailable" value={counts.unavailable} accent="text-danger" />
              <InfoCard label="Unknown" value={counts.unknown} accent="text-ink-muted" />
              <InfoCard
                label="Proxy service"
                value={proxyServiceStatus.isDown ? 'Down' : 'OK'}
                accent={proxyServiceStatus.isDown ? 'text-danger' : 'text-success'}
                hint={proxyServiceStatus.isDown ? proxyServiceStatus.description : undefined}
              />
            </div>

            <Card className="flex flex-col">
              {/* Toolbar */}
              <div className="shrink-0 flex flex-wrap items-center gap-3 p-4 border-b border-line">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by name, store URL…"
                  width={230}
                />
                <SortSelect options={SORT_OPTIONS} value={sort} onChange={setSort} />
                <StatusSegmented
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  counts={counts}
                />
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <div className="min-w-[1380px]">
                  {tableHeader}

                  {visibleApps.length === 0 ? (
                    <div className="py-12 text-center text-sm text-ink-muted">No apps found.</div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={visibleApps.map((a) => a._id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {visibleApps.map((app) => (
                          <SortableAppRow
                            key={app._id}
                            app={app}
                            index={apps.findIndex((a) => a._id === app._id)}
                            totalCount={apps.length}
                            dragEnabled={dragEnabled}
                            onNavigate={navigate}
                            onEdit={handleEditApp}
                            onDelete={setDeleteTarget}
                            onMoveUp={handleMoveUp}
                            onMoveDown={handleMoveDown}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>

              {/* Mobile card list */}
              <div className="md:hidden divide-y divide-line">
                {visibleApps.length === 0 ? (
                  <div className="py-12 text-center text-sm text-ink-muted">No apps found.</div>
                ) : (
                  visibleApps.map((app) => {
                    const index = apps.findIndex((a) => a._id === app._id);
                    const iconSrc = app.iconPath || app.iconUrl;
                    return (
                      <div key={app._id} className="p-4">
                        <div className="flex justify-between items-start gap-3 mb-3">
                          <div
                            className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer"
                            onClick={() => navigate(`/apps/${app._id}`)}
                          >
                            {iconSrc && (
                              <img
                                src={iconSrc}
                                alt=""
                                className="w-10 h-10 rounded-lg object-cover border border-line shrink-0"
                              />
                            )}
                            <div className="min-w-0">
                              <h3 className="text-[15px] font-medium text-ink truncate">{app.name}</h3>
                              <p className="text-xs text-ink-muted truncate">
                                {STORE_TYPE_LABELS[app.storeType] || ''}
                              </p>
                            </div>
                          </div>
                          <StatusBadge status={app.lastCheckStatus} />
                        </div>

                        <div className="flex flex-col gap-2 text-xs">
                          <div>
                            <span className="text-ink-muted">Store URL</span>
                            <a
                              href={app.storeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-brand-500 break-all hover:underline"
                            >
                              {app.storeUrl}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-ink-muted">Proxy</span>
                            <ProxyBadge app={app} />
                          </div>
                          <div>
                            <span className="text-ink-muted">Last checked</span>
                            <p className="text-ink">{formatDate(app.lastCheckedAt)}</p>
                          </div>
                          <div>
                            <span className="text-ink-muted">Last unavailable</span>
                            <p className="text-ink">{formatDate(app.lastUnavailableCheckAt)}</p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 mt-3 border-t border-line">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                              aria-label="Move up"
                              className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-ink disabled:text-ink-disabled"
                            >
                              <IconArrowUp size={14} />
                            </button>
                            <button
                              onClick={() => handleMoveDown(index)}
                              disabled={index === apps.length - 1}
                              aria-label="Move down"
                              className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-ink disabled:text-ink-disabled"
                            >
                              <IconArrowDown size={14} />
                            </button>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEditApp(app)}
                              aria-label="Edit app"
                              className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-brand-500 hover:bg-surface"
                            >
                              <IconEdit size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(app)}
                              aria-label="Delete app"
                              className="w-8 h-8 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-danger hover:bg-surface"
                            >
                              <IconTrash size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </>
        )}
        </div>
      </div>

      {showModal && (
        <AppModal
          app={editingApp}
          onClose={handleModalClose}
        />
      )}

      <DeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemType="app"
        itemName={deleteTarget?.name}
      />
    </div>
  );
};

export default Apps;
